import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import PullToRefresh from './PullToRefresh';
import NotificationDisplay from './NotificationDisplay';
import OnlineStatusIndicator from './OnlineStatusIndicator';
import UploadQueueIndicator from './UploadQueueIndicator';
import { TabBar } from './ui/TabBar';

export const FullScreenContext = createContext({
  isFullScreen: false,
  setIsFullScreen: (value: boolean) => {},
  hideHeader: false,
  setHideHeader: (value: boolean) => {},
});

export const FixedElementsContext = createContext({
  registerFixedElement: (element: React.ReactNode) => {},
  unregisterFixedElement: () => {},
  fixedElements: [] as React.ReactNode[]
});

export const RefreshContext = createContext({
  registerRefreshHandler: (_handler: () => Promise<void>) => {},
  unregisterRefreshHandler: () => {},
});

export const useFullScreen = () => useContext(FullScreenContext);
export const useFixedElements = () => useContext(FixedElementsContext);
export const useRefresh = () => useContext(RefreshContext);

const Layout: React.FC = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [fixedElements, setFixedElements] = useState<React.ReactNode[]>([]);
  const refreshHandlerRef = useRef<(() => Promise<void>) | null>(null);

  const registerFixedElement = (element: React.ReactNode) => {
    setFixedElements(prev => [...prev, element]);
    return fixedElements.length;
  };

  const unregisterFixedElement = () => {
    setFixedElements([]);
  };

  const registerRefreshHandler = useCallback((handler: () => Promise<void>) => {
    refreshHandlerRef.current = handler;
  }, []);

  const unregisterRefreshHandler = useCallback(() => {
    refreshHandlerRef.current = null;
  }, []);

  const handleRefresh = async () => {
    if (refreshHandlerRef.current) {
      await refreshHandlerRef.current();
    } else {
      window.location.reload();
    }
  };

  return (
    <FullScreenContext.Provider value={{ isFullScreen, setIsFullScreen, hideHeader, setHideHeader }}>
      <FixedElementsContext.Provider value={{ registerFixedElement, unregisterFixedElement, fixedElements }}>
        <RefreshContext.Provider value={{ registerRefreshHandler, unregisterRefreshHandler }}>
          <div className="min-h-screen bg-[var(--bg-primary)]">
            {/* Offline banner. Outside the isFullScreen guard on purpose: the
                camera runs fullscreen, and that is exactly where a worker most
                needs to know captures are queueing locally. */}
            <OnlineStatusIndicator />

            {/* Desktop sidebar - always visible on lg+ */}
            <Sidebar />

            {/* Mobile header - hidden in fullscreen or when subpage has own nav */}
            {!isFullScreen && !hideHeader && (
              <div className="lg:hidden">
                <Header />
              </div>
            )}

            {/* Main content area: always offset by sidebar width on desktop */}
            <div className="lg:ml-[240px] flex flex-col min-h-screen pb-tabbar">
              <PullToRefresh onRefresh={handleRefresh}>
                <main className="flex-1 max-w-7xl mx-auto w-full">
                  <Outlet />
                </main>
              </PullToRefresh>
            </div>

            <div className="fixed-elements-container">
              {fixedElements}
            </div>

            <NotificationDisplay />

            {/* Background photo uploads follow the user between pages. Hidden
                while the camera is fullscreen — it shows its own shot status. */}
            <UploadQueueIndicator hidden={isFullScreen} />

            {/* Mobile tab bar */}
            {!isFullScreen && <TabBar />}
          </div>
        </RefreshContext.Provider>
      </FixedElementsContext.Provider>
    </FullScreenContext.Provider>
  );
};

export default Layout;
